import { LuActivity, LuBoxes, LuBuilding2, LuClipboardList, LuFactory, LuGauge, LuHardDrive, LuMapPin, LuPackage, LuPlane, LuRadio, LuRefreshCw, LuSettings, LuSignal } from 'react-icons/lu';

const BAG_COUNTER_KEY = "conveyor-belt-live-bag-counter-v1";
const DEFAULT_BAG_COUNTER = {
  target: 1000,
  durationMinutes: 60,
  startedAt: null,
};
const PAGE_SIZE = 10;
const WORKSPACE_COLLECTIONS = [
  "countries",
  "states",
  "airports",
  "iataCodes",
  "approaches",
  "belts",
  "bagCounts",
  "sensors",
  "bearings",
  "systems",
  "cellPhones",
  "plcs",
  "radios",
  "displays",
  "activity",
];
const STATUS = ['Active', 'Inactive'];
const BELT_STATUS = ['Running', 'Stopped', 'Fault', 'Maintenance', 'Offline / Unknown'];
const DATE_TIME_LOCAL_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2})?$/;
const FIELD_MAX_LENGTHS = {
  countries: { name: 200, isoCode: 3, classification: 50, phoneCode: 20, currency: 10, status: 30 },
  states: { name: 200, code: 20, countryId: 100, status: 30 },
  airports: { name: 200, type: 30, countryId: 100, stateId: 100, city: 150, address: 2000, status: 30 },
  iataCodes: { code: 3, airportId: 100, status: 30 },
  approaches: { name: 200, code: 50, countryId: 100, stateId: 100, airportId: 100, terminal: 100, zone: 200, description: 2000, status: 30 },
  belts: { beltId: 100, name: 200, countryId: 100, stateId: 100, airportId: 100, terminal: 100, approachId: 100, type: 50, runningStatus: 50, status: 30 },
  beltRunning: { beltId: 100, runningStatus: 50 },
  bagCounts: { airportId: 100, terminal: 100, beltId: 100, sensorId: 100, eventId: 200, flightNumber: 30, countSource: 50 },
  sensors: { sensorId: 100, name: 200, type: 50, airportId: 100, approachId: 100, beltId: 100, plcId: 100, connectionStatus: 30, faultStatus: 30 },
  bearings: { bearingId: 100, type: 100, beltId: 100, location: 200, condition: 30, notes: 4000 },
  systems: { companyName: 200, adminEmail: 254, userRoles: 1000, passwordPolicy: 1000, notifications: 50, timeZone: 100, integrationStatus: 50, backupSchedule: 50, systemStatus: 50 },
  cellPhones: { deviceId: 100, operator: 200, mobileNumber: 50, deviceName: 200, airportId: 100, notifications: 50, connectionStatus: 30 },
  plcs: { plcId: 100, name: 200, address: 200, protocol: 50, airportId: 100, approachId: 100, beltId: 100, connectionStatus: 30, faultCode: 100 },
  radios: { radioId: 100, staffAssignment: 200, airportId: 100, approachId: 100, deviceStatus: 30, communicationGroup: 100, maintenanceLog: 4000 },
  displays: { displayId: 100, name: 200, airportId: 100, terminal: 100, approachId: 100, beltId: 100, displayType: 100, connectionStatus: 30, screenLayout: 300 },
};
const emptyDb = () => ({
  countries: [],
  states: [],
  airports: [],
  iataCodes: [],
  approaches: [],
  belts: [],
  bagCounts: [],
  sensors: [],
  bearings: [],
  systems: [],
  cellPhones: [],
  plcs: [],
  radios: [],
  displays: [],
  activity: [],
  liveBagCounter: DEFAULT_BAG_COUNTER,
});

const FIELDS = {
  countries: [
    ["name", "Country name", "text", true, "e.g. India"],
    ["isoCode", "ISO country code", "text", true, "e.g. IN"],
    [
      "classification",
      "Country type",
      "select",
      true,
      ["Domestic base", "International"],
    ],
    ["phoneCode", "Phone code", "text", false, "e.g. +91"],
    ["currency", "Currency", "text", false, "e.g. INR"],
    ["status", "Status", "select", true, STATUS],
  ],
  states: [
    ["name", "State name", "text", true, "Enter state / province"],
    ["code", "State code", "text", true, "e.g. GJ"],
    ["countryId", "Country", "relation", true, "countries"],
    ["status", "Status", "select", true, STATUS],
  ],
  airports: [
    ["name", "Airport name", "text", true, "Enter official airport name"],
    ["type", "Airport type", "select", true, ["Domestic", "International"]],
    ["countryId", "Country", "relation", true, "countries"],
    ["stateId", "State / province", "relation", true, "states"],
    ["city", "City", "text", true, "Enter city"],
    [
      "address",
      "Address",
      "textarea",
      false,
      "Street address or airport location",
    ],
    ["status", "Status", "select", true, STATUS],
  ],
  iataCodes: [
    ["code", "IATA code", "text", true, "Three letters, e.g. AMD"],
    ["airportId", "Airport", "relation", true, "airports"],
    ["status", "Status", "select", true, STATUS],
  ],
  approaches: [
    ["name", "Location name", "text", true, "e.g. Arrival Baggage Area"],
    ["code", "Location code", "text", true, "e.g. AMD-T1-ARR"],
    ["countryId", "Country", "relation", true, "countries"],
    ["stateId", "State", "relation", true, "states"],
    ["airportId", "Airport", "relation", true, "airports"],
    ["terminal", "Terminal", "text", true, "e.g. Terminal 1"],
    ["zone", "Area / zone", "text", true, "e.g. Arrivals · East wing"],
    ["description", "Description", "textarea", false, "Location details"],
    ["status", "Status", "select", true, STATUS],
  ],
  belts: [
    ["beltId", "Unique belt ID", "text", true, "e.g. AMD-T1-B01"],
    ["name", "Belt name / number", "text", true, "e.g. Baggage belt 01"],
    ["countryId", "Country", "relation", true, "countries"],
    ["stateId", "State / province", "relation", true, "states"],
    ["airportId", "Airport", "relation", true, "airports"],
    ["terminal", "Terminal", "text", true, "e.g. Terminal 1"],
    ["approachId", "Approach location", "relation", true, "approaches"],
    [
      "type",
      "Belt type",
      "select",
      true,
      ["Arrival", "Departure", "Transfer", "Reclaim"],
    ],
    ["installationDate", "Installation date", "date", true, ""],
    ["runningStatus", "Operating status", "select", true, BELT_STATUS],
    ["status", "Registration status", "select", true, STATUS],
  ],
  beltRunning: [
    ["beltId", "Belt ID", "relation", true, "belts"],
    ["runningStatus", "Operating status", "select", true, BELT_STATUS],
    ["lastUpdated", "Last status update", "datetime-local", true, ""],
  ],
  bagCounts: [
    ["airportId", "Airport", "relation", true, "airports"],
    ["terminal", "Terminal", "text", true, "e.g. Terminal 1"],
    ["beltId", "Belt", "relation", true, "belts"],
    ["sensorId", "Sensor ID", "relation", false, "sensors"],
    ["currentCount", "Current count", "number", true, "0"],
    ["totalCount", "Total count", "number", true, "0"],
    ["recordedAt", "Date / time", "datetime-local", true, ""],
    [
      "eventId",
      "Sensor / counter event ID",
      "text",
      false,
      "Required for sensor / PLC events",
    ],
    ["flightNumber", "Flight number", "text", false, "e.g. AI 173"],
    [
      "countSource",
      "Count source",
      "select",
      true,
      ["Sensor / PLC", "Manual entry", "Import"],
    ],
  ],
  sensors: [
    ["sensorId", "Sensor ID", "text", true, "e.g. AMD-SEN-01"],
    ["name", "Sensor name", "text", true, "e.g. Arrival photo-eye"],
    [
      "type",
      "Sensor type",
      "select",
      true,
      ["Photo-eye", "Proximity", "Barcode", "Weighing", "Other"],
    ],
    ["airportId", "Airport", "relation", true, "airports"],
    ["approachId", "Approach location", "relation", true, "approaches"],
    ["beltId", "Mapped belt", "relation", true, "belts"],
    ["plcId", "Mapped PLC", "relation", false, "plcs"],
    [
      "connectionStatus",
      "Connection status",
      "select",
      true,
      ["Online", "Offline"],
    ],
    ["lastSignal", "Last signal time", "datetime-local", false, ""],
    [
      "faultStatus",
      "Fault status",
      "select",
      true,
      ["Clear", "Warning", "Fault"],
    ],
  ],
  bearings: [
    ["bearingId", "Bearing ID", "text", true, "e.g. BRG-AMD-01"],
    ["type", "Bearing type / model", "text", true, "e.g. Deep groove 6205"],
    ["beltId", "Belt ID", "relation", true, "belts"],
    ["location", "Location", "text", true, "e.g. Drive end · east side"],
    ["installationDate", "Installation date", "date", true, ""],
    ["lastInspectionDate", "Last inspection date", "date", true, ""],
    ["nextMaintenanceDate", "Next maintenance date", "date", true, ""],
    [
      "condition",
      "Condition",
      "select",
      true,
      ["Good", "Warning", "Fault", "Maintenance"],
    ],
    ["notes", "Notes", "textarea", false, "Inspection and maintenance details"],
  ],
  systems: [
    ["companyName", "Company name", "text", true, "Company / operator name"],
    ["adminEmail", "Administrator email", "email", true, "admin@example.com"],
    [
      "userRoles",
      "User roles",
      "text",
      true,
      "e.g. Admin, Operator, Maintenance",
    ],
    [
      "passwordPolicy",
      "Password policy",
      "text",
      true,
      "e.g. 12+ characters, MFA required",
    ],
    [
      "notifications",
      "Fault notifications",
      "select",
      true,
      ["Enabled", "Disabled"],
    ],
    [
      "timeZone",
      "Time zone",
      "select",
      true,
      ["Asia/Kolkata", "UTC", "Asia/Dubai", "Asia/Singapore"],
    ],
    [
      "integrationStatus",
      "PLC / API integrations",
      "select",
      true,
      ["Not connected", "Configured", "Connected"],
    ],
    [
      "backupSchedule",
      "Backup schedule",
      "select",
      true,
      ["Daily", "Weekly", "Monthly", "Manual"],
    ],
    [
      "systemStatus",
      "System status",
      "select",
      true,
      ["Operational", "Degraded", "Maintenance"],
    ],
  ],
  cellPhones: [
    ["deviceId", "Device ID", "text", true, "e.g. MOB-AMD-01"],
    ["operator", "Employee / operator", "text", true, "Assigned staff member"],
    [
      "mobileNumber",
      "Mobile number (restricted)",
      "tel",
      false,
      "Authorized admin only",
    ],
    ["deviceName", "Device name", "text", true, "e.g. Belt supervisor phone"],
    ["airportId", "Airport", "relation", true, "airports"],
    [
      "notifications",
      "Notification preference",
      "select",
      true,
      ["All alerts", "Faults only", "Disabled"],
    ],
    [
      "connectionStatus",
      "Connection status",
      "select",
      true,
      ["Connected", "Offline"],
    ],
    ["lastActive", "Last active time", "datetime-local", false, ""],
  ],
  plcs: [
    ["plcId", "PLC ID", "text", true, "e.g. PLC-AMD-01"],
    ["name", "PLC name", "text", true, "Enter controller name"],
    ["address", "IP / device address", "text", true, "e.g. 192.168.10.21"],
    [
      "protocol",
      "Protocol",
      "select",
      true,
      ["Modbus TCP", "OPC UA", "EtherNet/IP", "Other"],
    ],
    ["airportId", "Airport", "relation", true, "airports"],
    ["approachId", "Approach location", "relation", true, "approaches"],
    ["beltId", "Connected belt", "relation", true, "belts"],
    [
      "connectionStatus",
      "Connection status",
      "select",
      true,
      ["Online", "Offline"],
    ],
    ["lastCommunication", "Last communication", "datetime-local", false, ""],
    ["faultCode", "Fault code", "text", false, "None"],
  ],
  radios: [
    ["radioId", "Device / radio ID", "text", true, "e.g. WT-AMD-01"],
    [
      "staffAssignment",
      "Staff assignment",
      "text",
      true,
      "Assigned operator / team",
    ],
    ["airportId", "Airport", "relation", true, "airports"],
    ["approachId", "Location", "relation", true, "approaches"],
    [
      "deviceStatus",
      "Device status",
      "select",
      true,
      ["Available", "Assigned", "Fault", "Maintenance"],
    ],
    [
      "communicationGroup",
      "Communication group",
      "text",
      true,
      "e.g. Baggage Operations",
    ],
    [
      "maintenanceLog",
      "Fault / maintenance log",
      "textarea",
      false,
      "Maintenance history",
    ],
  ],
  displays: [
    ["displayId", "Display ID", "text", true, "e.g. DSP-AMD-01"],
    ["name", "Display name", "text", true, "e.g. Arrival belt board"],
    ["airportId", "Airport", "relation", true, "airports"],
    ["terminal", "Terminal", "text", true, "e.g. Terminal 1"],
    ["approachId", "Location", "relation", true, "approaches"],
    ["beltId", "Connected belt", "relation", true, "belts"],
    [
      "displayType",
      "Display type",
      "select",
      true,
      ["Belt status", "Baggage information", "Operations dashboard"],
    ],
    [
      "connectionStatus",
      "Connection status",
      "select",
      true,
      ["Online", "Offline"],
    ],
    [
      "screenLayout",
      "Screen layout",
      "text",
      true,
      "e.g. Belt + baggage count + alerts",
    ],
    ["lastUpdated", "Last updated time", "datetime-local", false, ""],
  ],
};

const MODULES = [
  {
    key: "dashboard",
    label: "Dashboard",
    icon: LuGauge,
    group: "Overview",
    columns: [],
  },
  {
    key: "approaches",
    label: "Approach Location",
    icon: LuMapPin,
    group: "Airport masters",
    description: "Manage terminal approach zones and baggage belt locations.",
    columns: [
      ["name", "Location"],
      ["code", "Code"],
      ["airportId", "Airport", "airports"],
      ["terminal", "Terminal"],
      ["zone", "Area / zone"],
      ["status", "Status"],
    ],
  },
  {
    key: "airports",
    label: "Airport Name",
    icon: LuPlane,
    group: "Airport masters",
    description: "Maintain domestic and international airport profiles.",
    columns: [
      ["name", "Airport"],
      ["type", "Type"],
      ["countryId", "Country", "countries"],
      ["stateId", "State", "states"],
      ["city", "City"],
      ["status", "Status"],
    ],
  },
  {
    key: "iataCodes",
    label: "IATA Code",
    icon: LuClipboardList,
    group: "Airport masters",
    description: "Assign unique three-letter codes to registered airports.",
    columns: [
      ["code", "IATA code"],
      ["airportId", "Airport", "airports"],
      ["countryId", "Country", "airportCountry"],
      ["stateId", "State", "airportState"],
      ["city", "City", "airportCity"],
      ["status", "Status"],
    ],
  },
  {
    key: "states",
    label: "State",
    icon: LuMapPin,
    group: "Airport masters",
    description: "Maintain state and province records under each country.",
    columns: [
      ["name", "State"],
      ["code", "State code"],
      ["countryId", "Country", "countries"],
      ["status", "Status"],
    ],
  },
  {
    key: "countries",
    label: "Country",
    icon: LuBuilding2,
    group: "Airport masters",
    description:
      "Manage country master data used by domestic and international records.",
    columns: [
      ["name", "Country"],
      ["isoCode", "ISO"],
      ["classification", "Classification"],
      ["phoneCode", "Phone code"],
      ["currency", "Currency"],
      ["status", "Status"],
    ],
  },
  {
    key: "belts",
    label: "Belt Total Quantity",
    icon: LuBoxes,
    group: "Belt operations",
    description:
      "Register belts; installed quantity is calculated from registered belts.",
    columns: [
      ["beltId", "Belt ID"],
      ["name", "Belt name"],
      ["airportId", "Airport", "airports"],
      ["stateId", "State", "airportState"],
      ["terminal", "Terminal"],
      ["approachId", "Approach", "approaches"],
      ["type", "Type"],
      ["beltSensorId", "Mapped sensor", "beltSensor"],
      ["beltPlcId", "Mapped PLC", "beltPlc"],
      ["runningStatus", "Live status"],
      ["status", "Registration"],
    ],
  },
  {
    key: "beltRunning",
    label: "Belt Running Quantity",
    icon: LuActivity,
    group: "Belt operations",
    description: "Monitor running, stopped, faulted and maintenance belts.",
    columns: [
      ["beltId", "Belt ID", "beltCode"],
      ["beltName", "Belt name", "beltName"],
      ["airportId", "Airport", "beltAirport"],
      ["runningStatus", "Operating status"],
      ["lastUpdated", "Last status update"],
    ],
  },
  {
    key: "bagCounts",
    label: "Bags Count",
    icon: LuPackage,
    group: "Belt operations",
    description: "Review bag counter events, source and belt assignment.",
    columns: [
      ["airportId", "Airport", "airports"],
      ["terminal", "Terminal"],
      ["beltId", "Belt", "beltCode"],
      ["currentCount", "Current"],
      ["totalCount", "Total"],
      ["recordedAt", "Recorded at"],
      ["eventId", "Event ID"],
      ["flightNumber", "Flight"],
      ["countSource", "Source"],
    ],
  },
  {
    key: "sensors",
    label: "Sensor",
    icon: LuSignal,
    group: "Equipment",
    description:
      "Register and monitor baggage detection sensors and signal health.",
    columns: [
      ["sensorId", "Sensor ID"],
      ["name", "Sensor name"],
      ["type", "Type"],
      ["beltId", "Belt", "beltCode"],
      ["plcId", "PLC", "plcCode"],
      ["connectionStatus", "Connection"],
      ["lastSignal", "Last signal"],
      ["faultStatus", "Fault"],
    ],
  },
  {
    key: "bearings",
    label: "Bearing",
    icon: LuRefreshCw,
    group: "Equipment",
    description:
      "Track bearing condition, inspections and upcoming maintenance.",
    columns: [
      ["bearingId", "Bearing ID"],
      ["type", "Model"],
      ["beltId", "Belt", "beltCode"],
      ["lastInspectionDate", "Last inspection"],
      ["nextMaintenanceDate", "Next maintenance"],
      ["condition", "Condition"],
    ],
  },
  {
    key: "systems",
    label: "System",
    icon: LuSettings,
    group: "Administration",
    description:
      "Manage company, access policy, integrations, notifications and backup settings.",
    columns: [
      ["companyName", "Company"],
      ["adminEmail", "Admin email"],
      ["notifications", "Notifications"],
      ["timeZone", "Time zone"],
      ["integrationStatus", "Integrations"],
      ["backupSchedule", "Backup"],
      ["systemStatus", "System status"],
    ],
  },
  {
    key: "cellPhones",
    label: "Cell Phone",
    icon: LuActivity,
    group: "Equipment",
    description:
      "Manage staff devices, notification preferences and connectivity.",
    columns: [
      ["deviceId", "Device ID"],
      ["operator", "Operator"],
      ["mobileNumber", "Mobile number"],
      ["deviceName", "Device"],
      ["airportId", "Airport", "airports"],
      ["notifications", "Alerts"],
      ["connectionStatus", "Connection"],
      ["lastActive", "Last active"],
    ],
  },
  {
    key: "plcs",
    label: "PLC",
    icon: LuFactory,
    group: "Equipment",
    description:
      "Monitor controller endpoints, connected belts and communications.",
    columns: [
      ["plcId", "PLC ID"],
      ["name", "Controller"],
      ["address", "Device address"],
      ["protocol", "Protocol"],
      ["beltId", "Connected belt", "beltCode"],
      ["connectionStatus", "Connection"],
      ["lastCommunication", "Last communication"],
      ["faultCode", "Fault code"],
    ],
  },
  {
    key: "radios",
    label: "Quickie – Talkie",
    icon: LuRadio,
    group: "Equipment",
    description:
      "Manage operational radio inventory, staff assignments and maintenance.",
    columns: [
      ["radioId", "Radio ID"],
      ["staffAssignment", "Staff / team"],
      ["airportId", "Airport", "airports"],
      ["approachId", "Location", "approaches"],
      ["communicationGroup", "Group"],
      ["deviceStatus", "Device status"],
    ],
  },
  {
    key: "displays",
    label: "Display",
    icon: LuHardDrive,
    group: "Equipment",
    description: "Configure belt-status and baggage information screens.",
    columns: [
      ["displayId", "Display ID"],
      ["name", "Display"],
      ["airportId", "Airport", "airports"],
      ["terminal", "Terminal"],
      ["beltId", "Connected belt", "beltCode"],
      ["displayType", "Display type"],
      ["connectionStatus", "Connection"],
      ["lastUpdated", "Last updated"],
    ],
  },
];


export {
  BAG_COUNTER_KEY,
  BELT_STATUS,
  DEFAULT_BAG_COUNTER,
  DATE_TIME_LOCAL_PATTERN,
  FIELD_MAX_LENGTHS,
  FIELDS,
  MODULES,
  PAGE_SIZE,
  STATUS,
  WORKSPACE_COLLECTIONS,
  emptyDb,
};
